import { useEffect, useState } from 'react'
import { Loader2, Wallet, CheckCircle2, MapPin, Clock } from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import WorkerLayout from '@/components/worker/WorkerLayout'

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n != null && !isNaN(n)
    ? '₱' + Number(n).toLocaleString('en-PH', { minimumFractionDigits: 0 })
    : '₱0'

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-PH', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function cityOf(address) {
  if (!address) return ''
  const parts = address.split(',')
  return parts.length > 1 ? parts[parts.length - 2]?.trim() : parts[0]?.trim()
}

function currentMonthLabel() {
  return new Date().toLocaleDateString('en-PH', { month: 'long', year: 'numeric' })
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function WorkerEarnings() {
  const profile = useAuthStore((s) => s.profile)

  const [jobs,      setJobs]      = useState([])
  const [loading,   setLoading]   = useState(true)
  const [available, setAvailable] = useState(false)

  useEffect(() => {
    if (!profile?.id) return
    fetchEarnings()
    fetchAvailability()
  }, [profile?.id]) // eslint-disable-line

  async function fetchAvailability() {
    const { data } = await supabase
      .from('worker_profiles')
      .select('is_available')
      .eq('id', profile.id)
      .maybeSingle()
    if (data) setAvailable(data.is_available ?? false)
  }

  async function fetchEarnings() {
    setLoading(true)
    const { data } = await supabase
      .from('bookings')
      .select(`
        id, ref_number, worker_payout, completed_at, address,
        service_categories ( name )
      `)
      .eq('worker_id', profile.id)
      .eq('status', 'completed')
      .not('worker_payout', 'is', null)
      .order('completed_at', { ascending: false })
    setJobs(data ?? [])
    setLoading(false)
  }

  // Totals
  const totalAll = jobs.reduce((sum, j) => sum + Number(j.worker_payout ?? 0), 0)

  const now = new Date()
  const thisMonthJobs = jobs.filter((j) => {
    if (!j.completed_at) return false
    const d = new Date(j.completed_at)
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  })
  const totalMonth = thisMonthJobs.reduce((sum, j) => sum + Number(j.worker_payout ?? 0), 0)

  return (
    <WorkerLayout available={available}>
      <div className="max-w-lg mx-auto px-4 py-5 flex flex-col gap-5">

        {/* ── Summary cards ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
            <p className="text-xs text-ink-muted mb-1">This Month</p>
            <p className="text-xl font-bold text-siglat-teal">{fmt(totalMonth)}</p>
            <p className="text-[10px] text-ink-muted mt-0.5">{currentMonthLabel()}</p>
          </div>
          <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
            <p className="text-xs text-ink-muted mb-1">Total Earned</p>
            <p className="text-xl font-bold text-ink-primary">{fmt(totalAll)}</p>
            <p className="text-[10px] text-ink-muted mt-0.5">{jobs.length} job{jobs.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* ── Info note ─────────────────────────────────────────────────────── */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-3 flex items-start gap-2.5">
          <Wallet className="w-4 h-4 text-siglat-teal shrink-0 mt-0.5" />
          <p className="text-xs text-ink-secondary leading-relaxed">
            Payouts are released after admin verifies job completion. Contact admin for payout schedules to your registered GCash/Maya/bank account.
          </p>
        </div>

        {/* ── Job history ───────────────────────────────────────────────────── */}
        <div>
          <p className="text-xs text-ink-muted uppercase tracking-widest font-semibold mb-3 px-1">
            Completed Jobs
          </p>

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 text-siglat-teal animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-surface-raised border border-surface-border rounded-xl p-8 text-center">
              <Wallet className="w-8 h-8 text-ink-muted mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium text-ink-secondary">No completed jobs yet</p>
              <p className="text-xs text-ink-muted mt-1">Your earnings will appear here after jobs are verified.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-surface-raised border border-surface-border rounded-xl p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-ink-primary">
                          {job.service_categories?.name ?? 'Service'}
                        </p>
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" />
                      </div>
                      <p className="text-[10px] text-ink-muted font-mono mb-1.5">{job.ref_number}</p>
                      <div className="flex items-center gap-3 flex-wrap">
                        {job.completed_at && (
                          <span className="flex items-center gap-1 text-xs text-ink-muted">
                            <Clock className="w-3 h-3 shrink-0" />
                            {fmtDate(job.completed_at)}
                          </span>
                        )}
                        {job.address && (
                          <span className="flex items-center gap-1 text-xs text-ink-muted">
                            <MapPin className="w-3 h-3 shrink-0" />
                            {cityOf(job.address)}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-base font-bold text-siglat-teal shrink-0">
                      {fmt(job.worker_payout)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </WorkerLayout>
  )
}
